import 'regenerator-runtime/runtime'
import React, { useCallback, useEffect, useState } from 'react'
import AuthContext from './AuthContext'
import { AppProviderProps, AuthorizationsLoadState } from '../types'
import { cjPlatformAuth as cjAuth } from '../auth'
import { authorizationsApi } from '../services/authorizationsApi'

type AuthProviderInternalProps = AppProviderProps & {
  authorizationsApiGet?: (config: { baseUrl: string; getBearerToken: () => Promise<string> }) => Promise<Response>
}

const AuthProvider = ({ children, authorizationsApiGet }: AuthProviderInternalProps): JSX.Element => {
  const [authorizations, setAuthorizations] = useState<string[]>([])
  const [authorizationsState, setAuthorizationsState] = useState<AuthorizationsLoadState>('loading')

  const fetchAuthorizations = useCallback(async () => {
    setAuthorizationsState('loading')
    const apiGet = authorizationsApiGet ?? authorizationsApi.get
    try {
      const response = await apiGet({
        baseUrl: process.env.REACT_APP_ADMIN_TOOL_API_URL ?? '',
        getBearerToken: cjAuth.getBearerToken,
      })

      if (response.status === 403) {
        setAuthorizationsState('unauthorized')
        return
      }

      if (!response.ok) {
        setAuthorizationsState('error')
        return
      }

      const data = await response.json()
      const caps: string[] = data.authorizations ?? []
      setAuthorizations(caps)
      setAuthorizationsState(caps.length > 0 ? 'authorized' : 'unauthorized')
    } catch {
      setAuthorizationsState('error')
    }
  }, [authorizationsApiGet])

  useEffect(() => {
    fetchAuthorizations()
  }, [fetchAuthorizations])

  const value = {
    authorizations,
    authorizationsState,
    retryAuthorizations: fetchAuthorizations,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export default AuthProvider
